<template>
	<div class="site-settings">
		<div class="site-settings__container">
			<div class="logo-block">
				<div class="logo-block__container">
					<div class="logo-block__logo">
						<div
							v-if="(!site.logo || isEditingLogo) && $store.state.auth.role === 'admin'"
							class="logo-block__upload"
						>
							<LogoUpload
								:text="$t('loadFile')"
								@change="changeLogo"
							/>
						</div>
						<img
							v-else
							class="logo-block__img"
							:src="site.logo"
						>
					</div>
					<div class="logo-block__controls">
						<vs-button
							v-if="$store.state.auth.role === 'admin'"
							:disabled="!site.logo"
							@click="isEditingLogo = !isEditingLogo"
							class="logo-block__btn w-full pl-1 pr-1"
						>
							{{ isEditingLogo ? this.$t('cancel') : this.$t('changeLogo') }}
						</vs-button>

						<vs-custom-button
							v-if="$store.state.auth.role === 'admin'"
							:disabled="!site.logo"
							@click="site.logo = ''"
							type="border"
							color="danger"
							class="logo-block__btn w-full pl-1 pr-1"
						>{{ $t('deleteLogo') }}</vs-custom-button>
					</div>
				</div>
			</div>
			<div class="site-settings__inputs vx-row">
				<div class="vx-col w-full sm:w-1/2">
					<vs-input
						v-model="site.name"
						v-validate="'required|min:3'"
						name="name"
						:label="$t('name')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('name')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/2">
					<vs-input
						v-model="site.domain"
						v-validate="'required'"
						:label="$t('site')"
						class="site-settings__input w-full"
						name="site"
					/>
					<span v-if="errors.first('site')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/2">
					<vs-input
						v-model="site.uid"
						v-validate="'required|min:3'"
						name="uid"
						:label="`${$t('site')} ID`"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('uid')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/2">
					<vs-input
						v-model="site.emailDisguise"
						v-validate="'required|min:3'"
						name="emailDisguise"
						:label="`${$t('emailFrontSite')}`"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('emailDisguise')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div v-if="$store.state.auth.role === 'admin'" class="vx-col w-full">
					<vs-input
						v-model="site.stripeApi"
						v-validate="'required|min:3'"
						name="stripeApi"
						:label="`${$t('Stripe API')}`"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('stripeApi')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div v-if="$store.state.auth.role === 'admin'" 
					style="padding-top: 40px;"
					class="vx-col w-full sm:w-1/2"
				>
					<vs-checkbox
						v-model="site.specialPrice"
						class="w-full"
					>{{ $t('specialPriceForAuthors') }}</vs-checkbox>
				</div>
				<div v-if="$store.state.auth.role === 'admin'" 
					style="padding-top: 40px;"
					class="vx-col w-full sm:w-1/2"
				>
					<vs-checkbox
						v-model="site.isPartnerWebsite"
						class="w-full"
					>{{ $t('isPartnerWebsite') }}</vs-checkbox>
				</div>
				<div class="vx-col w-full sm:w-1/2">
					<vs-color
						v-validate="'required'"
						:pickerColor="site.highlightingText"
						name="highlightingText"
						v-model="site.highlightingText"
						:label="$t('highlightingText')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('highlightingText')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/2">
					<vs-color
						v-validate="'required'"
						:pickerColor="site.highlightingBackground"
						name="highlightingBackground"
						v-model="site.highlightingBackground"
						:label="$t('highlightingBackground')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('highlightingBackground')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div v-if="$store.state.auth.role === 'admin'" class="vx-col w-full sm:w-full">
					<hr v-if="$store.state.auth.role === 'admin'" class="mt-4">
					<vs-input
						v-model="site.email"
						v-validate="'required|email'"
						name="email"
						:label="$t('emailIncomingUserName')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('email')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div v-if="$store.state.auth.role === 'admin'" class="vx-col w-full sm:w-1/2">
					<vs-input
						v-model="site.email_host"
						v-validate="'required'"
						:label="$t('email_host')"
						class="site-settings__input w-full"
						name="email_host"
					/>
					<span v-if="errors.first('email_host')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div v-if="$store.state.auth.role === 'admin'" class="vx-col w-full sm:w-1/2">
					<vs-password
						v-model="site.email_password"
						v-validate="'required|min:6'"
						name="password"
						type="password"
						:label="$t('passIncomingEmail')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('password')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div v-if="$store.state.auth.role === 'admin'" class="vx-col w-full sm:w-1/2">
					<vs-input
						v-model="site.emailPort"
						v-validate="'required'"
						:label="$t('emailPort')"
						class="site-settings__input w-full"
						name="emailPort"
					/>
					<span v-if="errors.first('emailPort')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div v-if="$store.state.auth.role === 'admin'" class="vx-col w-full sm:w-1/2">
					<vs-input
						v-model="site.emailSecure"
						v-validate="'required'"
						:label="$t('emailSecure')"
						class="site-settings__input w-full"
						name="emailSecure"
					/>
					<span v-if="errors.first('emailSecure')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div v-if="$store.state.auth.role === 'admin'" class="vx-col w-full sm:w-full">
					<hr class="mt-4">
					<vs-input
						v-model="site.emailOutgoingUserName"
						v-validate="'required'"
						name="emailOutgoingUserName"
						:label="$t('emailOutgoingUserName')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('emailOutgoingUserName')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div v-if="$store.state.auth.role === 'admin'" class="vx-col w-full sm:w-1/2">
					<vs-input
						v-model="site.emailOutgoingHost"
						v-validate="'required'"
						:label="$t('emailOutgoingHost')"
						class="site-settings__input w-full"
						name="emailOutgoingHost"
					/>
					<span v-if="errors.first('emailOutgoingHost')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div v-if="$store.state.auth.role === 'admin'" class="vx-col w-full sm:w-1/2">
					<vs-password
						v-model="site.emailOutgoingUserPassword"
						v-validate="'required|min:6'"
						name="emailOutgoingUserPassword"
						type="password"
						:label="$t('emailOutgoingUserPassword')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('emailOutgoingUserPassword')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div v-if="$store.state.auth.role === 'admin'" class="vx-col w-full sm:w-1/2">
					<vs-input
						v-model="site.emailOutgoingPort"
						v-validate="'required'"
						:label="$t('emailOutgoingPort')"
						class="site-settings__input w-full"
						name="emailOutgoingPort"
					/>
					<span v-if="errors.first('emailOutgoingPort')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div v-if="$store.state.auth.role === 'admin'" class="vx-col w-full sm:w-1/2">
					<vs-input
						v-model="site.emailOutgoingSecure"
						v-validate="'required'"
						:label="$t('emailOutgoingSecure')"
						class="site-settings__input w-full"
						name="emailOutgoingSecure"
					/>
					<span v-if="errors.first('emailOutgoingSecure')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>

				<div class="vx-col w-full sm:w-1/3">
					<hr v-if="$store.state.auth.role === 'admin'" class="mt-4">
					<vs-phone
						v-model="site.phone"
						v-validate="'required|min:7|max:14'"
						:label="$t('phone')"
						class="site-settings__input w-full"
						name="phone"
					/>
					<span v-if="errors.first('phone')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/3">
					<hr class="mt-4">
					<vs-color
						v-validate="'required'"
						:pickerColor="site.theme_color_main"
						name="main_color"
						v-model="site.theme_color_main"
						:label="$t('mainColor')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('main_color')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/3">
					<hr class="mt-4">
					<vs-color
						v-validate="'required'"
						:pickerColor="site.theme_color_secondary"
						name="additional_color"
						v-model="site.theme_color_secondary"
						:label="$t('additionalColor')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('additional_color')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/3">
					<vs-input
						v-model="site.commission_manager_percent"
						v-validate="'required|numeric'"
						name="manager_percent"
						:label="$t('managersFee')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('manager_percent')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/3">
					<vs-input
						v-model="site.commissionManagerSecondPercent"
						v-validate="'required|numeric'"
						name="commissionManagerSecondPercent"
						:label="$t('managersFeeSecond')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('commissionManagerSecondPercent')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/3">
					<vs-input
						v-model="site.commissionGeneralManagerPercent"
						v-validate="'required|numeric'"
						name="general_manager_percent"
						:label="$t('managersGeneralFee')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('general_manager_percent')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import LogoUpload from "@/components/LogoUpload";
import databaseManager from "@/mixins/databaseManager";
import notifier from "@/mixins/notifier";

export default {
	name: "SiteSettings",
	mixins: [databaseManager, notifier],
	props: {
		site: { required: true, type: Object },
		validate: {required: true, type: Boolean }
	},
	components: {
		LogoUpload
	},
	data() {
		return {
			isEditingLogo: false
		};
	},
	watch: {
		validate() {
			this.makeValidate();
		}
	},
	methods: {
		makeValidate() {
			if(!this.validate) return;

			const entries = Object.entries(this.site);
			if(entries.length && this.validate) {
				this.$validator.validateAll();
			}
		},
		async changeLogo(path, files) {
			try {
				const data = [files[files.length - 1]];
				const res = await this.dbUploadMany(data);
				this.site.logo = res[0].fullUrl;
				this.isEditingLogo = false;

			} catch(e) {
				this.notifyError(e.message);
			}
		}
	},
	mounted() {
		this.makeValidate();
	}
};
</script>

<style lang="scss" scoped>
.site-settings {
	&__container {
		display: flex;
	}

	.logo-block {
		flex: 198px 0 0;

		&__container {
			width: 198px;
		}

		&__upload {
			width: 100%;
			height: 100%;
		}

		&__btn {
			margin-top: 15px
		}

		&__logo {
			display: flex;
			justify-content: center;
			align-items: center;
			width: 200px;
			height: 87.5px;
			background-color: #eeeeee;
			border-radius: 10px;
			overflow: hidden;
		}

		&__img {
			max-width: 100%;
			max-height: 100%;
		}
	}

	&__inputs {
		margin-top: -17px;
		margin-left: 20px;
	}

	&__input {
		margin-top: 17px;
	}

	@media screen and (max-width: 992px) {
		&__container {
			flex-wrap: wrap;
		}

		.logo-block {
			flex: 100%;

			&__container {
				display: flex;
				width: 100%;
			}

			&__controls {
				flex-grow: 1;
				max-width: 180px;
				margin-left: 15px;
			}
		}

		&__inputs {
			margin-top: 20px;
			margin-left: -1rem;
		}
	}

	@media screen and (max-width: 576px) {
		.logo-block {
			&__container {
				flex-direction: column;
				align-items: center;
			}

			&__controls {
				max-width: 198px;
				margin-left: 0;
			}
		}
	}
}
</style>
